import type { TouchEvent } from "react";
import { useCallback, useRef } from "react";

type Point = { x: number; y: number };

export const useLongPress = (props: {
  onLongPress: () => void;
  enabled?: boolean;
  durationMs?: number;
  moveThresholdPx?: number;
  onTouchStart?: () => void;
}) => {
  const { onLongPress, enabled = true, durationMs = 1000, moveThresholdPx = 10, onTouchStart } = props;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRef = useRef<Point | null>(null);
  const isPressingRef = useRef(false);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startRef.current = null;
    isPressingRef.current = false;
  }, []);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!enabled) return;
      const touch = e.touches[0];
      startRef.current = { x: touch.clientX, y: touch.clientY };
      isPressingRef.current = true;
      onTouchStart?.();

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        startRef.current = null;
        isPressingRef.current = false;
        onLongPress();
      }, durationMs);
    },
    [durationMs, enabled, onLongPress, onTouchStart],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!enabled) return;
      if (!timerRef.current || !startRef.current) return;
      const touch = e.touches[0];
      const dx = touch.clientX - startRef.current.x;
      const dy = touch.clientY - startRef.current.y;
      if (Math.hypot(dx, dy) > moveThresholdPx) {
        clear();
      }
    },
    [clear, enabled, moveThresholdPx],
  );

  const handleTouchEnd = useCallback(() => {
    if (!enabled) return;
    clear();
  }, [clear, enabled]);

  return {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    onTouchCancel: handleTouchEnd,
    clear,
    isPressingRef,
  };
};
